import {
  Table, TableBody, TableCell, TableHead, TableRow,
  TableSortLabel, Tooltip, TextField, Button, TableContainer, CircularProgress, Select
} from '@mui/material';
import React, { useEffect, useState, useRef } from 'react';
import moment from 'moment';
import styles from './UserTableComponent.module.scss';
import CheckCircleTwoToneIcon from '@mui/icons-material/CheckCircleTwoTone';
import DoDisturbOnTwoToneIcon from '@mui/icons-material/DoDisturbOnTwoTone';
import {deleteUser, getUsersList, updateUser} from '../../../../api/users';
import BoltTwoToneIcon from '@mui/icons-material/BoltTwoTone';
import HorizontalRuleTwoToneIcon from '@mui/icons-material/HorizontalRuleTwoTone';
import DeleteTwoToneIcon from '@mui/icons-material/DeleteTwoTone';
import MenuItem from '@mui/material/MenuItem';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import MonetizationOnTwoToneIcon from '@mui/icons-material/MonetizationOnTwoTone';
import Switch from '@mui/material/Switch';

const columns = [
  { id: 'username', label: 'Nickname' },
  { id: 'email', label: 'Email' },
  { id: 'birthdate', label: 'Birth date' },
  { id: 'lastSignInDate', label: 'Last time in the app' },
  { id: 'createdDate', label: 'Join date' },
  { id: 'xp', label: 'XP' },
  { id: 'energy', label: 'Energy' },
  { id: 'coins', label: 'Coins'},
  { id: 'chatBlocked', label: 'Chat is blocked'},
  { id: 'numberChallenges', label: 'Number of challenges' },
  { id: 'disabled', label: 'Active' },
];

const sortByValue = [
  {
    id: 'firstname',
    label: 'First name'
  },
  {
    id: 'lastname',
    label: 'Last name'
  },
  {
    id: 'email',
    label: 'Email'
  },
  {
    id: 'username',
    label: 'User name'
  },
  {
    id: 'energy',
    label: 'Energy'
  },
  {
    id: 'xp',
    label: 'XP'
  },
  {
    id: 'createdDate',
    label: 'Date created'
  },
  {
    id: 'lastSignInDate',
    label: 'Last sign in date'
  }
];

const sortByOrder = [
  {
    id: 'asc',
    label: 'Ascending (smallest value first)'
  },
  {
    id: 'desc',
    label: 'Descending (largest value first)'
  }
]

export const UsersTableComponent = () => {
  const [userList, setUserList] = useState<any>([]);
  const [searchQueryByEmail, setSearchQueryByEmail] = useState('');
  const [searchQueryByUsername, setSearchQueryByUsername] = useState('');
  const tableEl = useRef<any>(null);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [distanceBottom, setDistanceBottom] = useState(0);
  const [sortBy, setSortBy] = useState(sortByValue[0].id);
  const [sortOrder, setSortOrder] = useState(sortByOrder[0].id);
  const [isShowClearFilterButton, setIsShowClearFilterButton] = useState(false);
  
  useEffect(() => {
    (async () => {
      await onSearchUser();
    })()
  }, [sortBy, sortOrder]);
  
  const loadMore = () => {
    const loadItems = async () => {
      await new Promise(resolve =>
        setTimeout(() => {
          getMoreUsers();
          resolve(true);
        }, 2000)
      )
    };
    
    loadItems();
  };
  
  useEffect(() => {
    const scrollListener = () => {
      let bottom = tableEl?.current?.scrollHeight - tableEl?.current?.clientHeight;
    
      if (!distanceBottom) {
        setDistanceBottom(Math.round(bottom * 0.2));
      }
    
      if ((tableEl.current.scrollTop > bottom - distanceBottom) && hasMore && !loading) {
        loadMore();
        setLoading(true);
      }
    };
    
    if (tableEl && tableEl.current) {
      tableEl.current.addEventListener('scroll', scrollListener, false);
  
      return () => tableEl?.current?.removeEventListener('scroll', scrollListener, false);
    }
  }, [tableEl.current, userList, distanceBottom]);
  
  const getMoreUsers = async () => {
    const params = {
      ...(searchQueryByUsername && {username: searchQueryByUsername}),
      ...(searchQueryByEmail && {email: searchQueryByEmail}),
      cursor: userList[userList.length - 1].id,
      orderDirection: sortOrder,
      sortBy
    };
    
    const newUserList = await getUsersList(params);
    setUserList([...userList, ...newUserList]);
    if (!newUserList.length) setHasMore(false);
    setLoading(false);
  };
  
  const changeEnergy = (newEnergy: string, userId: string) => {
    const userData = userList?.find((item: any) => item.id === userId);
    const updatedUserList = userList.map((item: any) => {
      const energy = Number(newEnergy) ?? 0;
      
      if (item.id === userId) return {...userData, energy};
      return item;
    })
    
    setUserList(updatedUserList);
  };
  
  const changeCoins = (newCoins: string, userId: string) => {
    const userData = userList?.find((item: any) => item.id === userId);
    const updatedUserList = userList.map((item: any) => {
      const coins = Number(newCoins) ?? 0;
      
      if (item.id === userId) return {...userData, coins};
      return item;
    })
    
    setUserList(updatedUserList);
  };
  
  const changeChatBlocked = async (isBlocked: boolean, user: any) => {
    const userData = userList?.find((item: any) => item.id === user.id);
    const updatedUserList = userList.map((item: any) => {
      if (item.id === user.id) return {...userData, chatBlocked: isBlocked};
      return item;
    })
  
    setUserList(updatedUserList);
    await updateUser({...userData, chatBlocked: isBlocked});
  }
  
  const handleRowItem = (valueType: string, value: string | number | boolean, row?: any) => {
    switch (valueType) {
      case 'lastSignInDate':
      case 'createdDate': {
        return <span>{moment(value?.toString()).format('LL')}</span>
      }
      case 'isRegistered': {
        return value ? <CheckCircleTwoToneIcon color="success"/> : <DoDisturbOnTwoToneIcon color="error"/>
      }
      case 'disabled': {
        return !value ? <CheckCircleTwoToneIcon color="success"/> : <DoDisturbOnTwoToneIcon color="error"/>
      }
      case 'energy': {
        return <div className="flex items-center">
          <input
            className="w-[60px] pl-2 pr-2 text-center rounded-md border-none outline-none bg-black bg-opacity-5 text-black"
            value={value.toString()}
            onChange={(event) => changeEnergy(event.target.value, row.id)}
            onBlur={() => updateUser(row)}
            type="number"
          />
          <BoltTwoToneIcon color="info" />
        </div>
      }
      case 'coins': {
        return <div className="flex items-center">
          <input
            className="w-[60px] pl-2 pr-2 text-center rounded-md border-none outline-none bg-black bg-opacity-5 text-black mr-2"
            value={value.toString()}
            onChange={(event) => changeCoins(event.target.value, row.id)}
            onBlur={() => updateUser(row)}
            type="number"
          />
          <MonetizationOnTwoToneIcon color="warning" />
        </div>
      }
      case 'chatBlocked': {
        return <div className="flex items-center">
          <Switch
            key={row.id}
            checked={!!value}
            size="small"
            onChange={(event) => changeChatBlocked(event.target.checked, row)}
          />
        </div>
      }
      case 'xp': {
        return <span>{value}</span>
      }
      default: return <span>{value || <HorizontalRuleTwoToneIcon color="disabled" />}</span>;
    }
  };
  
  const handleTooltipPreview = (valueType: string, value: string | number | boolean) => {
    switch (valueType) {
      case 'lastSignInDate':
      case 'createdDate': {
        return moment(value.toString()).format('LL');
      }
      case 'disabled': {
        return !value ? 'Active' : 'Inactive';
      }
      case 'isRegistered': {
        return value ? 'Registered' : 'Unregistered';
      }
      case 'energy': {
        return  <span>{value ?? 0}</span>;
      }
      case 'xp': {
        return value.toString()
      }
      case 'coins': {
        return <span>{value ?? 0}</span>;
      }
      case 'chatBlocked': {
        return <span>{!!value ? 'Chat for user blocked' : 'Chat for the user is available'}</span>
      }
      default: return <span>{value || <DoDisturbOnTwoToneIcon color="warning"/>}</span>;
    }
  };
  
  const handleDeleteUser = async (userId: string) => {
    await deleteUser(userId);
    await onSearchUser();
  };
  
  const onSearchUser = async () => {
    const params = {
      ...(searchQueryByUsername && {username: searchQueryByUsername}),
      ...(searchQueryByEmail && {email: searchQueryByEmail}),
      orderDirection: sortOrder,
      sortBy
    };
  
    setUserList(await getUsersList(params));
    if (searchQueryByUsername.length || searchQueryByEmail.length) setIsShowClearFilterButton(true);
  };
  
  return (
    <div className="mt-10 w-full h-full">
      <div className="flex items-center mb-5">
        <TextField
          color="info"
          label="Search users by email"
          value={searchQueryByEmail}
          onChange={(event) => setSearchQueryByEmail(event.target.value)}
          className="w-[30%]"
          onBlur={() => {
            if(!searchQueryByEmail.length) onSearchUser()
          }}
        />
        <Button
          className="!ml-5"
          variant="contained"
          onClick={() => onSearchUser()}
        >
          Search
        </Button>
      </div>
      <div className="flex items-center mb-10">
        <TextField
          color="info"
          label="Search users by username"
          value={searchQueryByUsername}
          onChange={(event) => setSearchQueryByUsername(event.target.value)}
          className="w-[30%]"
          onBlur={() => {
            if (!searchQueryByUsername.length) onSearchUser()
          }}
        />
        <Button
          className="!ml-5"
          variant="contained"
          onClick={() => onSearchUser()}
        >
          Search
        </Button>
      </div>
      <div className="flex justify-end mb-3 border-b-[1px] border-solid border-[#808080] pb-3 mr-5">
        <div>
          <label id="sortBy" className="mr-2 font-bold">Sort by value</label>
          <Select
            labelId="sortBy"
            id="sort-by-select"
            value={sortBy}
            onChange={(event) => setSortBy(event.target.value)}
          >
            {sortByValue.map((value, index) => {
              return <MenuItem value={value.id} key={index.toString()}>{value.label}</MenuItem>
            })}
          </Select>
        </div>
        <div className="ml-10">
          <label id="sortByOrder" className="mr-2 font-bold">Sorting order</label>
          <Select
            labelId="sortByOrder"
            id="sort-by-order-select"
            value={sortOrder}
            onChange={(event) => setSortOrder(event.target.value)}
          >
            {sortByOrder.map((value, index) => {
              return <MenuItem value={value.id} key={index.toString()}>{value.label}</MenuItem>
            })}
          </Select>
        </div>
      </div>
      <div className="mb-5 font-bold w-[100%] border-b-[1px] border-solid border-[#808080] pb-3 mr-5 flex justify-between items-center">
        Number of users in the table: {userList.length}
        {isShowClearFilterButton && (
          <Button
            color="error"
            variant="outlined"
            onClick={async () => {
              setSearchQueryByUsername('');
              setSearchQueryByEmail('');
              await setUserList(await getUsersList({ orderDirection: sortOrder, sortBy }));
              setIsShowClearFilterButton(false);
            }}
          >
            Clear filters
            <FilterAltOffIcon color="error" className="ml-3" />
          </Button>
        )}
      </div>
      <TableContainer sx={{ maxHeight: "60vh", width: '100%', position: 'relative', marginBottom: '20px' }} ref={tableEl}>
        <Table stickyHeader aria-labelledby="tableTitle" size={"small"} className={styles.table}>
          <TableHead>
            <TableRow>
              {userList.length > 0 && columns.map((item: any) => (
                <TableCell
                  key={item.id}
                  align="center"
                  style={{ minWidth: 50 }}
                >
                  <TableSortLabel
                    className="capitalize font-bold"
                  >
                    {item.label}
                  </TableSortLabel>
                </TableCell>
              ))}
              {userList.length > 0 && (
                <>
                  <TableCell key="isRegistered" align="center">
                    Registered user
                  </TableCell>
                  <TableCell key="action" align="center">
                    Actions
                  </TableCell>
                </>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {userList.map((row: any, index: number) => {
              return (
                <TableRow hover tabIndex={-1} key={index.toString()} className={styles.tableRow}>
                  {columns.map((column) => {
                    return (
                      <TableCell key={column.id} align="center" className={styles.rowItemContainer}>
                        <Tooltip title={handleTooltipPreview(column.id, row[column.id])} placement="top">
                          {handleRowItem(column.id, row[column.id], row)}
                        </Tooltip>
                      </TableCell>
                    );
                  })}
                  <TableCell key="isRegistered" align="center" className={styles.rowItemContainer}>
                    <Tooltip title={handleTooltipPreview('isRegistered', row['email'])} placement="top">
                      {handleRowItem('isRegistered', row['email'])}
                    </Tooltip>
                  </TableCell>
                  <TableCell
                    key="delete"
                    align="center"
                    className={styles.rowItemContainer}
                    onClick={() => handleDeleteUser(row['id'])}
                  >
                    <Tooltip title={`Delete user ${row['username']}`} placement="top">
                      <DeleteTwoToneIcon color="error" />
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <div className={styles.loadingContainer}>
        {loading && <CircularProgress className={styles.loading} />}
      </div>
    </div>
  )
}